import { useMutation, useQueryClient } from '@tanstack/react-query';
import { normalizeId } from '../utils/idNormalization';
import { transcriptKeys } from '../transcripts/queries';
import { projectKeys } from './queries'; // assuming this is in the same directory
import QualAPI from 'utils/QualAPI';
import API from 'apis/API';

/**
 * Hook to create a new project
 * @returns {Object} Mutation object with mutate function to create a new project
 */
export function useCreateProject() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ name = "", description = null }) => 
      QualAPI.newProject(name, description).then(response => response.data),
    onSuccess: (data) => {
      // Invalidate projects list to include the new project
      queryClient.invalidateQueries({ queryKey: projectKeys.all });
      return data;
    }
  });
}

/**
 * Hook to update project cache data
 * @returns {Object} Mutation object with mutate function to update project cache
 */
export function useUpdateProjectCache() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, projectData }) => {
      // This is a cache-only operation, so we return a resolved promise
      return Promise.resolve({ projectId, projectData });
    },
    onSuccess: ({ projectId, projectData }) => {
      const normalizedProjectId = normalizeId(projectId);
      
      // Get the current project data from cache
      const currentProjectData = queryClient.getQueryData(
        projectKeys.detail(normalizedProjectId)
      );

      if ( !currentProjectData ) return;

      // Only update the project name, keeping all other fields unchanged
      const updatedProjectData = {
        ...currentProjectData,
        ...projectData
      }

      // Update the specific project cache with only the name change
      queryClient.setQueryData(
        projectKeys.detail(normalizedProjectId),
        updatedProjectData
      );

      // Optionally invalidate the projects list to trigger a refetch
      // queryClient.invalidateQueries({ queryKey: projectKeys.all });
    },
  });
}

/**
 * Hook to duplicate a project
 * @returns {Object} Mutation object with mutate function to duplicate a project
 */
export function useDuplicateProject() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (projectId) => QualAPI.duplicateProject(projectId).then(response => response.data),
    onSuccess: (data) => {
      // Invalidate projects list to include the new duplicated project
      queryClient.invalidateQueries({ queryKey: projectKeys.all });
      return data;
    }
  });
}

/**
 * Hook to archive a project
 * @returns {Object} Mutation object with mutate function to archive a project
 */
export function useArchiveProject() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (projectId) => API.post(`/api/projects/${projectId}/project_archives`).then(response => response.data),
    onSuccess: () => {
      // Invalidate projects list after successful archive
      queryClient.invalidateQueries({ queryKey: projectKeys.all });
    }
  });
}

/**
 * Hook to unarchive a project
 * @returns {Object} Mutation object with mutate function to unarchive a project
 */
export function useUnarchiveProject() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (projectId) => API.delete(`/api/projects/${projectId}/project_archives/${projectId}`).then(response => response.data),
    onSuccess: () => {
      // Invalidate projects list after successful unarchive
      queryClient.invalidateQueries({ queryKey: projectKeys.all });
    }
  });
}

/**
 * Hook to update a project name
 * @returns {Object} Mutation object with mutate function to update project name
 */
export function useUpdateProjectName() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ projectId, name }) => QualAPI.updateProjectName(projectId, name).then(response => response.data),
    
    // Optimistically update the cache before the request completes
    onMutate: async ({ projectId, name }) => {
      const normalizedProjectId = normalizeId(projectId);
      
      // Cancel any outgoing refetches to prevent overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: projectKeys.detail(normalizedProjectId) });
      
      // Snapshot the previous value
      const previousProject = queryClient.getQueryData(
        projectKeys.detail(normalizedProjectId)
      );
      
      // Optimistically update to the new value
      if (previousProject) {
        queryClient.setQueryData(
          projectKeys.detail(normalizedProjectId),
          { ...previousProject, name }
        );
      }
      
      // Return a context object with the snapshotted value
      return { previousProject, projectId: normalizedProjectId };
    },
    
    // If the mutation fails, use the context returned from onMutate to roll back
    onError: (err, variables, context) => {
      if (context?.previousProject) {
        queryClient.setQueryData(
          projectKeys.detail(context.projectId),
          context.previousProject
        );
      }
    },    
  });
}